﻿import { ReactiveStore } from './core/state/reactive.js';
import { searchService, SearchOptions, SearchResult } from './core/services/search-service.js';
import { renderFormat } from './core/utils/format-engine.js';
import { deepMerge, type ArrayOrDirective } from './core/utils/deep-merge.js';
import { toastService } from './core/services/toast-service.js';
import type { IToastService } from './core/services/toast-service.js';
import { i18n, type Translator, type TranslationOverrides, type LocalePack } from './core/i18n/i18n-service.js';
import { resolveLocale } from './core/i18n/locale.js';

export type FacetDataType = 'string' | 'dateTime';

export interface FieldConfig {
    key: string;
    visible: boolean | string;
    label: string;
    format: string;
}

export interface FacetValue {
    value: any;
    count: number;
    selected: boolean;
    filter: string;
}

export interface FacetData {
    key: string;
    label: string;
    schema: string;
    format?: string;
    dataType?: FacetDataType | string;
    order?: number;
    values: FacetValue[];
}

export interface SearchComponentConfig {
    filter?: string;
    size?: string | number;
    searchText?: string;
    title?: string;
    fields?: FieldConfig[];
    facets?: Array<string | { key: string; label?: string; schema?: string; format?: string; dataType?: FacetDataType | string; datatype?: FacetDataType | string; order?: number }>;
    orderBy?: string[];
    allowEmptySearch?: boolean | string;
    showSearchbox?: boolean | string;
    showResultsMetadata?: boolean | string;
    showPagination?: boolean | string;
    showFacets?: boolean | string;
    url?: string;
    apiBaseUri?: string;
    urlOverride?: string;
    anonymous?: boolean | string;
    view?: string;
    useQueryString?: boolean | string;
    /**
     * Per-instance string overrides. Accepts a nested partial dictionary (same
     * shape as a locale file) or a flat dot-path map. Overrides the resolved
     * locale for this component instance only.
     */
    translations?: TranslationOverrides;
    /**
     * URL of a locale pack to fetch and register before rendering. Use to
     * override an existing language or add a brand-new one at runtime. The
     * fetch is cached by URL, so instances sharing a source fetch once.
     */
    localeSrc?: string;
    /**
     * Inline locale pack (already-parsed JSON), registered without a fetch.
     * Same shape as the payload `localeSrc` would return.
     */
    localePack?: LocalePack;
    [key: string]: any;
}

/**
 * Shape accepted for an override layer (config-obj/json/src/schema). Identical
 * to {@link SearchComponentConfig} except array-valued keys may instead supply
 * an array directive ({@link ArrayOrDirective}) to extend the base array with
 * `$append`/`$patch` rather than replacing it wholesale.
 */
export type SearchComponentConfigOverride = Omit<Partial<SearchComponentConfig>, 'fields' | 'facets' | 'orderBy'> & {
    fields?: ArrayOrDirective<FieldConfig>;
    facets?: ArrayOrDirective<NonNullable<SearchComponentConfig['facets']>[number]>;
    orderBy?: ArrayOrDirective<string>;
};

export interface SearchState {
    loading: boolean;
    hasSearched: boolean;
    hasResults: boolean;
    searchText: string;
    results: Array<{ document: Record<string, any> }>;
    totalCount: number;
    fetchTime: string | null;
    currentPage: number;
    facetData: FacetData[];
    view: string;
}

export abstract class EumSearchBase extends HTMLElement {
    protected config: SearchComponentConfig = {};
    protected store: ReactiveStore<SearchState>;
    protected searchUrl = '/v1/Search/portal?searchText=';
    protected options: SearchOptions;
    protected renderFormat = renderFormat;
    /** Shared toast service — available to all subclasses for user notifications. */
    protected toast: IToastService = toastService;
    /**
     * Localized-string accessor. Rebound in parseConfig() to a translator that
     * carries this instance's config-based `translations` overrides on top of
     * the resolved locale. Subclasses call `this.t('namespace.key', { param })`.
     */
    protected t: Translator = (key, params) => i18n.t(key, params);
    private unsubscribe: (() => void) | null = null;
    private inited = false;
    private initPending = false;
    private shadowAttached = false;
    private eventAbortController: AbortController | null = null;
    private searchGeneration = 0;

    constructor() {
        super();
        this.store = new ReactiveStore<SearchState>({
            loading: false,
            hasSearched: false,
            hasResults: false,
            searchText: '',
            results: [],
            totalCount: 0,
            fetchTime: null,
            currentPage: 0,
            facetData: [],
            view: 'table',
        });
        this.options = {
            searchOptions: { includeTotalCount: true, filter: '', size: 25, skip: 0 },
        };
    }

    // ── Lifecycle ──────────────────────────────────────────────────────

  async connectedCallback(): Promise<void> {
        // Reconnect case: element was already initialized, then removed from
        // the DOM, and is now being re-attached. Re-establish the store
        // subscription that disconnectedCallback tore down and resume without
        // re-running parseConfig/render/onInit.
        if (this.inited) {
            if (!this.unsubscribe) {
                this.unsubscribe = this.store.subscribe((state, key) => {
                    this.onStateChange(state, key);
                });
            }
            return;
        }

        // When `defer-init` is present, wait for an explicit init() call
        // before running the lifecycle. If init() was already called before
        // the element attached, honor that queued request now.
        if (this.hasAttribute('defer-init') && !this.initPending) {
            return;
        }

        await this.runInitChain();
    }

  disconnectedCallback(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
    }

    /**
     * Public entry point paired with the `defer-init` attribute. Runs the
     * lifecycle (parseConfig -> render -> subscribe -> onInit) that
     * connectedCallback would run without the attribute.
     *
     * Idempotent: subsequent calls after the first are silent no-ops.
     * If called before the element is attached to the DOM, the request is
     * queued and honored automatically when connectedCallback fires.
     *
     * To rebuild after mutating the config, use `reinit()`.
     */
    async init(): Promise<void> {
        if (this.inited) return;

        if (!this.isConnected) {
            this.initPending = true;
            return;
        }

        await this.runInitChain();
    }

    /**
     * Tears the element down and rebuilds it. Use after mutating the object
     * referenced by `config-obj` (or after changing any config-* attributes)
     * so the new configuration takes effect.
     *
     * Store state is reset to defaults. Any in-flight search from the
     * previous generation discards its results. The layered config
     * precedence (inline snippet -> config-obj/json/src/schema -> individual
     * attributes) is re-applied on every rebuild, so a partial override in
     * config-obj only changes the keys it explicitly sets.
     *
     * No-op if the element has never been attached to the DOM.
     */
    async reinit(): Promise<void> {
        if (!this.isConnected) return;

        this.resetForReinit();
        await this.runInitChain();
    }

    private async runInitChain(): Promise<void> {
        if (this.inited) return;
        this.inited = true;

        await this.parseConfig();

        // attachShadow can only be called once per element. On reinit the
        // shadow root persists; render() will replace its innerHTML.
        if (!this.shadowAttached) {
            this.attachShadowRoot();
            this.shadowAttached = true;
        }

        this.render();

        this.unsubscribe = this.store.subscribe((state, key) => {
            this.onStateChange(state, key);
        });

        this.onInit();
    }

    private resetForReinit(): void {
        // Any in-flight search that resolves after this point observes a
        // bumped generation and discards its results.
        this.searchGeneration++;

        // Detach base-class event listeners registered via initCommonEvents.
        this.eventAbortController?.abort();
        this.eventAbortController = null;

        // Detach the store subscription; runInitChain will re-subscribe.
        this.unsubscribe?.();
        this.unsubscribe = null;

        // Reset config and derived values back to constructor defaults.
        this.config = {};
        this.searchUrl = '/v1/Search/portal?searchText=';
        this.options = {
            searchOptions: { includeTotalCount: true, filter: '', size: 25, skip: 0 },
        };

        // Reset store state. We've already unsubscribed, so no notifications
        // fire for these writes.
        this.store.batch((s) => {
            s.loading = false;
            s.hasSearched = false;
            s.hasResults = false;
            s.searchText = '';
            s.results = [];
            s.totalCount = 0;
            s.fetchTime = null;
            s.currentPage = 0;
            s.facetData = [];
            s.view = 'table';
        });

        // Clear the flag so runInitChain will actually execute the chain.
        this.inited = false;
    }

    // ── Config parsing ────────────────────────────────────────────────

    private async parseConfig(): Promise<void> {
        // Base config from inline script
        const inlineScript = this.querySelector('script[type="application/json"]');
        if (inlineScript?.textContent) {
            this.config = JSON.parse(inlineScript.textContent);
        }

        // Override layer: config-obj, config-json, config-src, or config-schema (first found wins)
        let overrides: SearchComponentConfigOverride | null = null;

        const configObj = this.getAttribute('config-obj');
        const configJson = this.getAttribute('config-json');
        const configSrc = this.getAttribute('config-src');
        const configSchema = this.getAttribute('config-schema');

        if (configObj) {
            let resolved: any = this.resolvePath(configObj);
            // Allow the resolved value to be a Promise (or any thenable) so consumers
            // can publish a pending fetch at the path and have parseConfig await it.
            if (resolved && typeof resolved.then === 'function') {
                resolved = await resolved;
            }
            overrides = resolved;
        } else if (configJson) {
            overrides = JSON.parse(configJson);
        } else if (configSrc) {
            overrides = await this.fetchConfig(configSrc);
        } else if (configSchema) {
            const raw = this.resolvePath(configSchema);
            overrides = typeof raw === 'string' ? JSON.parse(raw) : raw;
        }

        if (overrides) {
            this.config = deepMerge(this.config, overrides);
        }

        // Final override layer: individual attributes matching config keys
        this.applyAttributeOverrides();

        // Register any config-supplied locale pack (inline object first, then a
        // remote source) before resolving the active locale, so a brand-new or
        // overridden language is available to setLocale below. The remote fetch
        // is cached by URL in the i18n service, so instances that share one
        // source trigger a single request.
        if (this.config.localePack) {
            i18n.registerPack(this.config.localePack);
        }
        if (this.config.localeSrc) {
            await i18n.loadLocalePack(this.config.localeSrc);
        }

        // Resolve the active locale now that config + attributes are known, so
        // render()/onInit() (which run next in the init chain) emit localized
        // strings. Baked-in dictionaries make this synchronous.
        i18n.setLocale(resolveLocale(this, this.config));

        // Bind `this.t` to a translator carrying any per-instance config
        // overrides (config.translations), layered over the resolved locale.
        this.t = i18n.createTranslator(this.config.translations);

        if (this.config.url) this.searchUrl = this.config.url;
        if (this.config.apiBaseUri) this.searchUrl = this.config.apiBaseUri + (this.config.urlOverride || '/eum_api/v1/Search/users?searchText=');
        if (this.config.anonymous === false) this.searchUrl = '/api/EUMAPI/PostObject?apiUrl=search/users%3FsearchText=';
        if (this.config.filter) this.options.searchOptions.filter = this.config.filter;
        if (this.config.size) this.options.searchOptions.size = parseInt(String(this.config.size));
        if (this.config.orderBy) this.options.OrderBy = this.config.orderBy;
        if (this.config.fields) {
            this.options.Select = this.config.fields.map((f) => f.key);
        }
        if (this.config.view) {
            this.store.proxy.view = this.config.view;
        }

        const facets = this.config.facets || [];
        const facetData: FacetData[] = facets.map((f) => {
            if (typeof f === 'string') {
                return { key: f, label: this.t(f), schema: "{key} eq '{value}'", format: '{value}', dataType: 'string', values: [] };
            }
            const configuredDataType = (f as any).dataType ?? (f as any).datatype;
            const configuredOrder = (f as any).order;
            return {
                key: f.key,
                label: this.t(f.label || f.key),
                schema: f.schema || "{key} eq '{value}'",
                format: f.format || '{value}',
                dataType: this.normalizeFacetDataType(configuredDataType),
                order: typeof configuredOrder === 'number' ? configuredOrder : (configuredOrder != null && configuredOrder !== '' && !isNaN(Number(configuredOrder)) ? Number(configuredOrder) : undefined),
                values: [],
            };
        });
        // Stable sort matching the `fields` model: entries with a numeric
        // `order` sort ascending; entries without one default to the end
        // (Number.MAX_SAFE_INTEGER). Ties (and un-ordered entries) fall back to
        // the source array order via the original index.
        facetData
            .map((f, index) => ({ f, index }))
            .sort((a, b) => {
                const orderA = a.f.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.f.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB || a.index - b.index;
            })
            .forEach((entry, i) => { facetData[i] = entry.f; });
        this.store.proxy.facetData = facetData;
        if (facetData.length > 0) {
            this.options.Facets = facetData.map((f) => f.key);
        }
    }

    private async fetchConfig(url: string): Promise<SearchComponentConfigOverride | null> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                console.error(`Failed to fetch config from ${url}: ${response.status}`);
                return null;
            }
            return await response.json();
        } catch (error) {
            console.error(`Failed to fetch config from ${url}:`, error);
            return null;
        }
    }

    private applyAttributeOverrides(): void {
        const attrToKey: Record<string, string> = {
            'filter': 'filter',
            'size': 'size',
            'search-text': 'searchText',
            'title': 'title',
            'order-by': 'orderBy',
            'allow-empty-search': 'allowEmptySearch',
            'show-searchbox': 'showSearchbox',
            'show-results-metadata': 'showResultsMetadata',
            'show-pagination': 'showPagination',
            'show-facets': 'showFacets',
            'url': 'url',
            'view': 'view',
            'use-query-string': 'useQueryString',
            'api-base-uri': 'apiBaseUri',
            'url-override': 'urlOverride',
            'anonymous': 'anonymous',
        };

        for (const [attr, key] of Object.entries(attrToKey)) {
            const value = this.getAttribute(attr);
            if (value === null) continue;

            try {
                (this.config as any)[key] = JSON.parse(value);
            } catch {
                (this.config as any)[key] = value;
            }
        }
    }

    private resolvePath(path: string): any {
        return path.split('.').reduce((obj: any, key) => obj?.[key], window);
    }

    // ── Query string ───────────────────────────────────────────────────

    private get queryStringEnabled(): boolean {
        return this.checkBoolean(this.config.useQueryString, true);
    }

    /**
     * Reads URL query params and applies them to state/options.
     * Returns true if params were found that should trigger a search.
     */
    protected applyQueryParams(): boolean {
        if (!this.queryStringEnabled) return false;

        const params = new URLSearchParams(window.location.search);
        let shouldSearch = false;

        // ?q= → searchText
        const q = params.get('q');
        if (q !== null) {
            this.store.proxy.searchText = q;
            shouldSearch = true;
        }

        // ?p= → page/skip
        const p = params.get('p');
        if (p !== null) {
            const page = Math.max(1, parseInt(p) || 1);
            this.options.searchOptions.skip = (page - 1) * this.options.searchOptions.size!;
            shouldSearch = true;
        }

        // Facet params: any param matching a configured facet key
        const facetData = this.store.proxy.facetData.map((facet) => {
            const paramValues = params.getAll(facet.key);
            if (paramValues.length === 0) return facet;

            shouldSearch = true;
            return {
                ...facet,
                values: paramValues.map((value) => ({
                    value,
                    count: 0,
                    selected: true,
                    filter: this.buildFacetFilter(facet, value),
                })),
            };
        });

        if (facetData.some((f) => f.values.some((v) => v.selected))) {
            this.store.proxy.facetData = facetData;
            this.rebuildFilter();
        }

        this.syncSearchBoxValue();
        return shouldSearch;
    }

    /**
     * Updates the browser URL to reflect the current search state.
     * Uses replaceState to avoid polluting browser history on every search.
     */
    protected updateQueryParams(): void {
        if (!this.queryStringEnabled) return;

        const state = this.store.proxy;
        const params = new URLSearchParams(window.location.search);

        // Remove only keys managed by this component, preserving unrelated params.
        params.delete('q');
        params.delete('p');
        for (const facet of state.facetData) {
            params.delete(facet.key);
        }

        // q
        if (state.searchText) {
            params.set('q', state.searchText);
        }

        // Facet selections
        for (const facet of state.facetData) {
            const selected = facet.values.filter((v) => v.selected);
            for (const v of selected) {
                params.append(facet.key, String(v.value));
            }
        }

        // p (only if beyond page 1)
        if (state.currentPage > 1) {
            params.set('p', String(state.currentPage));
        }

        const qs = this.preserveValuelessParams(params.toString());
        const hash = window.location.hash || '';
        const newUrl = qs
            ? `${window.location.pathname}?${qs}${hash}`
            : `${window.location.pathname}${hash}`;

        window.history.replaceState(null, '', newUrl);
    }

    /**
     * URLSearchParams.toString() serializes a valueless param (e.g. `?edit`)
     * as `edit=`, appending a trailing `=`. That extra `=` breaks consumers
     * such as the Portal page-edit redirect. This restores the bare form for
     * any key that appeared without an `=` in the current URL.
     */
    private preserveValuelessParams(qs: string): string {
        if (!qs) return qs;

        // Keys present in the original query string with no `=` (bare flags).
        const rawSearch = window.location.search.replace(/^\?/, '');
        const valuelessKeys = new Set(
            rawSearch
                .split('&')
                .filter((segment) => segment.length > 0 && !segment.includes('='))
                .map((segment) => segment)
        );
        if (valuelessKeys.size === 0) return qs;

        return qs
            .split('&')
            .map((segment) => {
                if (segment.endsWith('=')) {
                    const key = segment.slice(0, -1);
                    if (valuelessKeys.has(key)) return key;
                }
                return segment;
            })
            .join('&');
    }

    // ── Search ─────────────────────────────────────────────────────────

    protected async search(text?: string, skip = 0): Promise<void> {
        const state = this.store.proxy;
        state.loading = true;
        state.searchText = text ?? state.searchText;
        this.syncSearchBoxValue();
        this.options.searchOptions.skip = skip;
        const generation = this.searchGeneration;

        try {
            const start = Date.now();
            const results = await searchService.search(this.searchUrl, state.searchText, this.options);

            // Discard results if a reinit happened while we were awaiting.
            if (generation !== this.searchGeneration) return;

            const fetchTime = ((Date.now() - start) / 1000).toFixed(2);

            this.store.batch((s) => {
                s.results = results.value;
                s.totalCount = results.totalCount;
                s.hasSearched = true;
                s.hasResults = results.totalCount > 0;
                s.fetchTime = results.totalCount > 0 ? fetchTime : null;
                s.loading = false;
                s.currentPage = skip === 0 ? 1 : Math.ceil(skip / this.options.searchOptions.size!) + 1;
            });

            if (results.facets) {
                await this.updateFacets(results.facets);
                if (generation !== this.searchGeneration) return;
            }

            this.updateQueryParams();
        } catch (error) {
            if (generation !== this.searchGeneration) return;
            console.error('Search failed:', error);
            this.store.batch((s) => {
                s.results = [];
                s.totalCount = 0;
                s.hasResults = false;
                s.loading = false;
            });
        }
  }

  private async getCSV() {
    const state = this.store.proxy;
    let options = {
      ...this.options
    };
    options.searchOptions = { ...this.options.searchOptions, ...{skip: 0,size: undefined} };

    try {
      const results = await searchService.searchCSV(this.searchUrl, state.searchText, options);
      const blob = await results.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Search failed:', error);
    }
  }

  private async getPDF() {
    const state = this.store.proxy;
    let options = {
      ...this.options
    };
    options.searchOptions = { ...this.options.searchOptions, ...{ skip: 0, size: 5000 } };

    try {
      const searchResults = await searchService.search(this.searchUrl, state.searchText, options);

      //TODO: Is this supposed to be in the search table class?
      const fields = this.visibleFields;
      const tableHead = this.visibleFields
        .map((f) => `<th>${this.escapeHTML(f.label)}</th>`)
        .join('');
      const tableBody = searchResults.value
        .map((item) => {
          const cells = fields
            .map((f) => {
              const value = item.document[f.key];
              const rendered = f.format
                ? this.renderFormat(f.format, item.document)
                : this.escapeHTML(String(value ?? ''));
              return `<td>${rendered}</td>`;
            })
            .join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');

      const html = `<div class="table-responsive">
                            <table class="table table-striped table-hover align-middle mb-0">
                                <thead><tr>${tableHead}</tr></thead>
                                <tbody>${tableBody}</tbody>
                            </table>
                        </div>`;

      const results = await searchService.htmlToPDF(html);
      const blob = await results.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    } catch (error) {
      console.error('PDF Export failed:', error);
    }
  }

    private async updateFacets(facets: Record<string, Array<{ value: any; count: number }>>): Promise<void> {
        const current = this.store.proxy.facetData;

        // Step 1: Update facet values from the primary search results, preserving selection state
        const updated = current.map((facet) => {
            const values = facets[facet.key];
            if (!values || values.length === 0) return { ...facet, values: [] };
            return {
                ...facet,
                values: values.map((v) => {
                    const existing = facet.values.find((e) => String(e.value) === String(v.value));
                    return {
                        value: v.value,
                        count: v.count,
                        selected: existing?.selected ?? false,
                        filter: this.buildFacetFilter(facet, v.value),
                    };
                }),
            };
        });

        // Step 2: For facets with selected values, make a secondary search
        // EXCLUDING that facet's filter to get the unfiltered values/counts
        const facetsWithSelections = updated.filter((f) =>
            f.values.some((v) => v.selected)
        );

        if (facetsWithSelections.length > 0) {
            const facetPromises = facetsWithSelections.map(async (facet) => {
                const filterExcludingThisFacet = this.getFacetFilter(facet.key);
                const baseFilter = this.config.filter || '';

                let combinedFilter = baseFilter;
                if (filterExcludingThisFacet) {
                    combinedFilter = combinedFilter
                        ? `${combinedFilter} and ${filterExcludingThisFacet}`
                        : filterExcludingThisFacet;
                }

                const secondaryOptions: SearchOptions = {
                    searchOptions: {
                        filter: combinedFilter,
                        includeTotalCount: true,
                        size: 0,
                        skip: 0,
                    },
                    Facets: this.options.Facets,
                    OrderBy: this.options.OrderBy,
                    Select: this.options.Select,
                };

                try {
                    const secondaryResults = await searchService.search(
                        this.searchUrl,
                        this.store.proxy.searchText,
                        secondaryOptions
                    );

                    if (secondaryResults.facets?.[facet.key]) {
                        const secondaryValues = secondaryResults.facets[facet.key];
                        const idx = updated.findIndex((f) => f.key === facet.key);
                        if (idx !== -1) {
                            updated[idx] = {
                                ...updated[idx],
                                values: secondaryValues.map((v) => {
                                    const existing = facet.values.find(
                                        (e) => String(e.value) === String(v.value)
                                    );
                                    return {
                                        value: v.value,
                                        count: v.count,
                                        selected: existing?.selected ?? false,
                                        filter: this.buildFacetFilter(facet, v.value),
                                    };
                                }),
                            };
                        }
                    }
                } catch (error) {
                    console.error(`Failed to fetch facet counts for ${facet.key}:`, error);
                }
            });

            await Promise.all(facetPromises);
        }

        this.store.proxy.facetData = updated;
    }

    private normalizeFacetDataType(raw: any): FacetDataType {
        return String(raw || '').toLowerCase() === 'datetime' ? 'dateTime' : 'string';
    }

    private getFacetDataType(facet: FacetData): FacetDataType {
        return this.normalizeFacetDataType((facet as any).dataType ?? (facet as any).datatype);
    }

    private buildFacetFilter(facet: FacetData, rawValue: any): string {
        const value = String(rawValue);
        const schemaWithKey = facet.schema.replace('{key}', facet.key);

        if (this.getFacetDataType(facet) === 'dateTime') {
            return schemaWithKey
                .replace(/'\{value\}'/g, value)
                .replace('{value}', value);
        }

        return schemaWithKey.replace('{value}', value);
    }

    // ── Facet interaction ──────────────────────────────────────────────

    protected getFacetFilter(excludedKey = ''): string {
        const parts: string[] = [];
        this.store.proxy.facetData.forEach((facet) => {
            if (facet.key === excludedKey) return;
            const selected = facet.values.filter((v) => v.selected);
            if (selected.length > 0) {
                const filters = selected.map((v) => v.filter);
                parts.push(filters.length > 1 ? '(' + filters.join(' or ') + ')' : filters[0]);
            }
        });
        return parts.join(' and ');
    }

    protected toggleFacet(facetKey: string, value: string): void {
        const facetData = this.store.proxy.facetData.map((f) => {
            if (f.key !== facetKey) return { ...f, values: [...f.values] };
            return {
                ...f,
                values: f.values.map((v) => {
                    if (String(v.value) === value) {
                        return { ...v, selected: !v.selected };
                    }
                    return { ...v };
                }),
            };
        });

        this.store.proxy.facetData = facetData;
        this.rebuildFilter();
    }

    protected clearFacets(): void {
        const facetData = this.store.proxy.facetData.map((f) => ({
            ...f,
            values: f.values.map((v) => ({ ...v, selected: false })),
        }));
        this.store.proxy.facetData = facetData;
        this.options.searchOptions.filter = this.config.filter || '';
    }

    protected rebuildFilter(): void {
        const facetFilter = this.getFacetFilter();
        const baseFilter = this.config.filter || '';
        this.options.searchOptions.filter = facetFilter
            ? baseFilter ? `${baseFilter} and ${facetFilter}` : facetFilter
            : baseFilter;
    }

    // ── Common event wiring ────────────────────────────────────────────

    protected initCommonEvents(root: ShadowRoot): void {
        this.eventAbortController = new AbortController();
        const signal = this.eventAbortController.signal;

        root.addEventListener('eum-search', ((e: CustomEvent) => {
            this.store.proxy.searchText = e.detail.searchText;
            this.search(e.detail.searchText);
        }) as EventListener, { signal });

        root.addEventListener('eum-page-change', ((e: CustomEvent) => {
            const skip = (e.detail.page - 1) * this.options.searchOptions.size!;
            this.search(undefined, skip);
        }) as EventListener, { signal });

        root.addEventListener('eum-facet-change', ((e: CustomEvent) => {
            this.toggleFacet(e.detail.facetKey, e.detail.value);
            this.search(undefined, 0);
        }) as EventListener, { signal });

        root.addEventListener('eum-facets-clear', () => {
            this.clearFacets();
            this.search(undefined, 0);
        }, { signal });

        root.addEventListener('eum-get-csv', () => {
            this.getCSV();
        }, { signal });
      root.addEventListener('eum-get-pdf', () => {
        this.getPDF();
      }, { signal });
    }

    // ── Helpers for subclasses ─────────────────────────────────────────

    protected checkBoolean(value: any, defaultValue = true): boolean {
        if (value === undefined || value === null) return defaultValue;
        if (value === true || value === 'true') return true;
        if (value === false || value === 'false') return false;
        return defaultValue;
    }

    protected escapeHTML(str: string): string {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    protected syncSearchBoxValue(): void {
        const searchBox = this.shadowRoot?.querySelector('eum-search-box');
        if (!searchBox) return;

        const searchText = this.store.proxy.searchText ?? '';
        if (searchText) {
            searchBox.setAttribute('search-text', searchText);
        } else {
            searchBox.removeAttribute('search-text');
        }
    }

    protected get visibleFields(): FieldConfig[] {
        return (this.config.fields || []).filter(
            (f) => f.visible === true || f.visible === 'true'
        );
    }

    // ── Subclass contract ──────────────────────────────────────────────

    protected abstract attachShadowRoot(): void;
    protected abstract render(): void;
    protected abstract onInit(): void;
    protected abstract onStateChange(state: SearchState, changedKey: keyof SearchState): void;
}